import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  CreditWalletKind,
  formatMicroCreditsAsCredits,
} from '@this+that/models';

import {
  useCreditPacks,
  useCreditsBalance,
} from '../../../../../../hooks/Credits';
import { IS_CREDITS_BALANCE_UI_ENABLED } from '../../../../../../pages/private/Billing/constants';
import { APP_PAGES } from '../../../../../../routes/constants';
import {
  CreditsBalanceDerived,
  CreditsCardVariant,
  formatCreditsAmount,
} from '../../../../../../utils/credits/creditsBalanceMath';

export interface UseCreditsBalanceRowResult {
  isEnabled: boolean;
  loading: boolean;
  derived: CreditsBalanceDerived | undefined;
  amountLabel: string;
  totalLabel: string;
  amountCount: number;
  isNoWallet: boolean;
  showAddLink: boolean;
  isBuyModalOpen: boolean;
  openBuyModal: () => void;
  closeBuyModal: () => void;
  walletKind: CreditWalletKind;
  goToCreditsPage: () => void;
}

/** Same hook/derivation the old sidebar card (1a) and the Flows chip (1c) use — only the presentation differs here. */
export function useCreditsBalanceRow(): UseCreditsBalanceRowResult {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);

  const { derived, loading } = useCreditsBalance(IS_CREDITS_BALANCE_UI_ENABLED);
  const { packs } = useCreditPacks(IS_CREDITS_BALANCE_UI_ENABLED);

  const isNoWallet = derived?.variant === CreditsCardVariant.NO_WALLET;

  const amountLabel = derived
    ? formatCreditsAmount(derived.balanceMicros, i18n.language)
    : '';
  const totalLabel = derived
    ? formatCreditsAmount(derived.totalMicros, i18n.language)
    : '';
  const amountCount = derived
    ? Math.round(formatMicroCreditsAsCredits(derived.balanceMicros))
    : 0;

  const showAddLink = !!derived && !isNoWallet && packs.length > 0;

  return {
    isEnabled: IS_CREDITS_BALANCE_UI_ENABLED,
    loading,
    derived,
    amountLabel,
    totalLabel,
    amountCount,
    isNoWallet,
    showAddLink,
    isBuyModalOpen,
    openBuyModal: () => setIsBuyModalOpen(true),
    closeBuyModal: () => setIsBuyModalOpen(false),
    walletKind: CreditWalletKind.USER,
    goToCreditsPage: () => navigate(APP_PAGES.CREDITS),
  };
}
